import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Stack, Group, Button, Text, Card, Title } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, EmptyState, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Select, SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { isApiError, type logs } from '@repletoryreach/sdk-api';
import { useLogQuery } from '../../../data/useLogStats';
import { ActiveFilterPills, type ActiveFilterPill } from '../../../components/ActiveFilterPills';
import { LogDetailDrawer } from '../components/LogDetailDrawer';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { FAMILIES, LEVELS, buildColumns } from '../columns';

const LIMIT = 100;
const STATUS_CLASSES = ['2xx', '3xx', '4xx', '5xx'] as const;

interface Draft {
  family: logs.LogFamily;
  service: string;
  level: string;
  env: string;
  correlation_id: string;
  since: string;
  until: string;
  status_class: string;
  q: string;
}

const EMPTY: Draft = {
  family: 'api', service: '', level: '', env: '', correlation_id: '',
  since: '', until: '', status_class: '', q: ''
};

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

/**
 * A `datetime-local` input yields a naive local string. The binder wants an
 * offset, so the local moment is resolved before it goes on the wire; without
 * this the server reads a South African afternoon as a UTC one.
 */
function toIso(local: string): string | undefined {
  if (!local) return undefined;
  const parsed = new Date(local);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/**
 * The power surface over `GET /v1/admin/logs`: every parameter the endpoint
 * binds, in one form, with the query mirrored into the URL so a query can be
 * pasted into an incident channel and reproduced exactly. `/logs` stays the
 * browse view.
 */
export default function LogsQueryPage() {
  const t = useT();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  const applied: Draft = useMemo(() => ({
    family: (params.get('family') as logs.LogFamily) || EMPTY.family,
    service: params.get('service') ?? '',
    level: params.get('level') ?? '',
    env: params.get('env') ?? '',
    correlation_id: params.get('correlation_id') ?? '',
    since: params.get('since') ?? '',
    until: params.get('until') ?? '',
    status_class: params.get('status_class') ?? '',
    q: params.get('q') ?? ''
  }), [params]);

  const [draft, setDraft] = useState<Draft>(applied);
  // Which result the detail panel is showing, as a position in the result set.
  const [selected, setSelected] = useState<number | null>(null);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const orgId = params.get('org_id') ?? '';
  const [orgDraft, setOrgDraft] = useState(orgId);
  const orgInvalid = orgDraft.trim().length > 0 && !isUuid(orgDraft);

  const opts: logs.LogQueryOptions = useMemo(() => ({
    family: applied.family,
    ...(applied.service && { service: applied.service }),
    ...(applied.level && { level: applied.level }),
    ...(applied.env && { env: applied.env }),
    ...(applied.correlation_id && { correlation_id: applied.correlation_id }),
    ...(toIso(applied.since) ? { since: toIso(applied.since) } : {}),
    ...(toIso(applied.until) ? { until: toIso(applied.until) } : {}),
    ...(applied.q && { q: applied.q }),
    ...(applied.family === 'api' && applied.status_class
      ? { status_class: applied.status_class as '2xx' | '3xx' | '4xx' | '5xx' }
      : {}),
    ...(orgId && isUuid(orgId) ? { org_id: orgId } : {}),
    limit: LIMIT
  }), [applied, orgId]);

  const { data, isLoading, error, refetch } = useLogQuery(opts);
  const rows = (data?.data ?? []) as Indexed<logs.LogRow>[];
  const denied = isApiError(error) && error.status === 403;
  const count = data?.meta?.count ?? rows.length;
  const capped = count >= LIMIT;

  const dirty =
    (Object.keys(EMPTY) as (keyof Draft)[]).some((k) => draft[k] !== applied[k]) ||
    orgDraft !== orgId;
  const hasQuery = params.toString().length > 0;

  function apply(e?: React.FormEvent) {
    e?.preventDefault();
    if (orgInvalid) return;
    const next = new URLSearchParams();
    for (const [key, value] of Object.entries(draft)) {
      if (value) next.set(key, String(value));
    }
    if (orgDraft.trim()) next.set('org_id', orgDraft.trim());
    setParams(next, { replace: false });
  }

  /*
   * The query that produced these rows lives in the URL, and the form below
   * shows the draft rather than what was applied. Without these pills an
   * operator who narrowed by service two navigations ago has no way to see
   * that a filter is still on except by re-reading every control.
   */
  const PILL_FIELD: Record<string, string> = {
    family: t('logs.query.form.family'),
    level: t('logs.query.form.level'),
    service: t('logs.query.form.service'),
    env: t('logs.filters.env'),
    correlation_id: t('logs.columns.correlation'),
    since: t('logs.query.form.since'),
    until: t('logs.query.form.until'),
    status_class: t('logs.query.form.statusClass'),
    q: t('common.search'),
    org_id: t('logs.query.form.orgId')
  };

  const pills: ActiveFilterPill[] = [];
  for (const [key, value] of params.entries()) {
    if (!value) continue;
    // `family` is always set and is the subject of the query rather than a
    // filter on it, so it is not offered as something to remove.
    if (key === 'family') continue;
    const label = key === 'level'
      ? t(LEVELS.find((l) => l.value === value)?.labelKey ?? '')
      : value;
    pills.push({ id: key, field: PILL_FIELD[key] ?? key, value: label });
  }

  function removePill(id: string) {
    const next = new URLSearchParams(params);
    next.delete(id);
    setDraft((current) => ({ ...current, [id]: '' }));
    if (id === 'org_id') setOrgDraft('');
    setParams(next, { replace: false });
  }

  function reset() {
    setDraft(EMPTY);
    setOrgDraft('');
    setParams(new URLSearchParams(), { replace: false });
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notifySuccess(t('logs.query.copiedLink'), t('logs.query.copiedLinkTitle'));
    } catch {
      // A denied clipboard is not worth an error toast: the URL is in the bar.
    }
  }

  /** The results as they arrived, with the query that produced them. */
  function exportJson() {
    const body = JSON.stringify({ query: opts, exported_at: new Date().toISOString(), rows }, null, 2);
    downloadText(body, `logs-query-${Date.now()}.json`, 'application/json');
    notifySuccess(t('logs.export.done', { count: rows.length }), t('logs.export.title'));
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('logs.title')}
        title={t('logs.query.title')}
        description={t('logs.query.description')}
        actions={
          <Group gap="sm" align="center">
            {/* The query is already in the URL, which is what makes it
                shareable; this hands that URL over rather than inventing a
                saved-query store the service does not have. */}
            <Button size="sm" variant="default" onClick={copyLink} disabled={!hasQuery}>
              {t('logs.query.copyLink')}
            </Button>
            <Button size="sm" variant="default" onClick={exportJson} disabled={rows.length === 0}>
              {t('logs.export.action')}
            </Button>
            <Button size="sm" variant="subtle" onClick={() => nav('/logs')}>{t('logs.query.backToLogs')}</Button>
          </Group>
        }
      />

      {denied && (
        <Alert color="warning" title={t('logs.denied.title')}>{t('logs.denied.body')}</Alert>
      )}

      <Card withBorder>
        {/* A form, so Enter in any field runs the query. Every control feeds
            one draft and one Apply, including family: it used to fire on
            change while its three neighbours waited, which made Apply look
            broken for that one control. */}
        <form onSubmit={apply}>
          <Stack gap="md">
            <FormSection title={t('logs.query.section.source')} hint={t('logs.query.section.sourceHint')}>
            <Group gap="sm" wrap="wrap" align="flex-start">
              <Select
                size="sm"
                label={t('logs.query.form.family')}
                value={draft.family}
                onChange={(e) => set('family', e.currentTarget.value as logs.LogFamily)}
                data={FAMILIES.map((f) => ({ value: f.value, label: t(f.labelKey) }))}
              />
              <Select
                size="sm"
                label={t('logs.query.form.level')}
                value={draft.level}
                onChange={(e) => set('level', e.currentTarget.value)}
                data={[{ value: '', label: t('logs.query.form.levelAny') }, ...LEVELS.map((l) => ({ value: l.value, label: t(l.labelKey) }))]}
              />
              <TextInput
                size="sm"
                label={t('logs.query.form.service')}
                placeholder={t('logs.query.form.servicePlaceholder')}
                value={draft.service}
                onChange={(e) => set('service', e.currentTarget.value)}
              />
              <TextInput
                size="sm"
                label={t('logs.filters.env')}
                placeholder={t('logs.query.form.envPlaceholder')}
                value={draft.env}
                onChange={(e) => set('env', e.currentTarget.value)}
              />
            </Group>
            </FormSection>

            <FormSection title={t('logs.query.section.correlate')} hint={t('logs.query.section.correlateHint')}>
            <Group gap="sm" wrap="wrap" align="flex-start">
              <TextInput
                size="sm"
                label={t('logs.columns.correlation')}
                placeholder={t('logs.query.form.correlationPlaceholder')}
                description={t('logs.query.form.correlationHint')}
                value={draft.correlation_id}
                onChange={(e) => set('correlation_id', e.currentTarget.value)}
                style={{ minWidth: 260 }}
              />
              <TextInput
                size="sm"
                label={t('logs.query.form.orgId')}
                placeholder={t('logs.query.form.orgIdPlaceholder')}
                description={t('logs.query.form.orgIdHint')}
                error={orgInvalid ? t('logs.query.form.orgIdInvalid') : undefined}
                value={orgDraft}
                onChange={(e) => setOrgDraft(e.currentTarget.value)}
                style={{ minWidth: 300 }}
              />
            </Group>
            </FormSection>

            <FormSection title={t('logs.query.section.window')} hint={t('logs.query.section.windowHint')}>
            <Group gap="sm" wrap="wrap" align="flex-start">
              <TextInput
                size="sm"
                type="datetime-local"
                label={t('logs.query.form.since')}
                description={t('logs.query.form.sinceHint')}
                value={draft.since}
                onChange={(e) => set('since', e.currentTarget.value)}
              />
              <TextInput
                size="sm"
                type="datetime-local"
                label={t('logs.query.form.until')}
                description={t('logs.query.form.untilHint')}
                value={draft.until}
                onChange={(e) => set('until', e.currentTarget.value)}
              />
              <TextInput
                size="sm"
                label={t('common.search')}
                placeholder={draft.family === 'query' ? t('logs.query.form.searchPlaceholderSql') : t('logs.query.form.searchPlaceholder')}
                value={draft.q}
                onChange={(e) => set('q', e.currentTarget.value)}
                style={{ minWidth: 240 }}
              />
            </Group>

            {/* `status_class` is an api-family parameter; the server ignores it
                elsewhere, so offering it there would promise a filter that
                does nothing. */}
            {draft.family === 'api' && (
              <Stack gap="xs">
                <Text role="caption" c="var(--rr-color-text-muted)">{t('logs.query.form.statusClass')}</Text>
                <SegmentedControl
                  value={draft.status_class}
                  onChange={(v) => set('status_class', v)}
                  data={[
                    { value: '', label: t('common.all') },
                    ...STATUS_CLASSES.map((s) => ({ value: s, label: s }))
                  ]}
                />
              </Stack>
            )}
            </FormSection>

            <Group gap="sm" justify="flex-end" align="center">
              {hasQuery && (
                <Button size="sm" variant="subtle" type="button" onClick={reset}>{t('action.reset')}</Button>
              )}
              <Button size="sm" type="submit" variant={dirty ? 'filled' : 'default'} disabled={orgInvalid}>
                {t('action.apply')}
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>

      {/* What is narrowing the results, directly under the form that set it. */}
      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={reset} />

      {/* The form holds a draft and the table holds the applied query, so an
          edited-but-unapplied form is a state the page has to announce. A
          caption at the bottom of the form was missed often enough that the
          table looked stale rather than un-applied. */}
      {dirty && <Alert color="info">{t('logs.query.unapplied')}</Alert>}

      {error && !denied && (
        <Alert color="error">
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <span>{t('logs.loadError')}</span>
            <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      <TableShell
        title={t('logs.query.resultsTitle')}
        totalLabel={t('logs.query.resultCount', { count, limit: LIMIT })}
        description={capped ? t('logs.capReached', { limit: LIMIT }) : undefined}
        loading={isLoading}
        isEmpty={!isLoading && !error && rows.length === 0}
        empty={
          <EmptyState
            title={hasQuery ? t('logs.empty') : t('logs.query.notRun')}
            description={hasQuery ? t('logs.emptyFiltered') : t('logs.query.notRunHint')}
            action={hasQuery ? <Button size="xs" variant="outline" onClick={reset}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
      >
        <DataTable<Indexed<logs.LogRow>>
          records={rows}
          loading={isLoading}
          idAccessor={(r, i) => `${r.id ?? i}`}
          columns={buildColumns(t, applied.family)}
          emptyState={t('logs.tableEmpty')}
          highlightOnHover
          onRowClick={(_, i) => setSelected(i)}
        />
      </TableShell>

      <LogDetailDrawer
        row={selected !== null ? rows[selected] ?? null : null}
        family={applied.family}
        onClose={() => setSelected(null)}
        onPrevious={selected !== null && selected > 0 ? () => setSelected(selected - 1) : undefined}
        onNext={selected !== null && selected < rows.length - 1 ? () => setSelected(selected + 1) : undefined}
      />
    </Stack>
  );
}

/**
 * A heading over each cluster of controls. The form binds nine parameters, and
 * nine inputs in one undifferentiated block is a form an operator reads rather
 * than scans.
 */
function FormSection({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <Stack gap="sm">
      <Stack gap="xs">
        <Title order={6}>{title}</Title>
        <Text role="caption" c="var(--rr-color-text-muted)">{hint}</Text>
      </Stack>
      {children}
    </Stack>
  );
}
